import React, { useState, useMemo, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import { Database, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';

ModuleRegistry.registerModules([AllCommunityModule]);

const darkTheme = themeQuartz.withParams({
  backgroundColor: '#0f172a',
  foregroundColor: '#e2e8f0',
  headerBackgroundColor: '#1e293b',
  headerForegroundColor: '#cbd5e1',
  borderColor: '#334155',
  rowHoverColor: '#1e293b',
  selectedRowBackgroundColor: '#1e3a5f',
  oddRowBackgroundColor: '#0f172a',
  chromeBackgroundColor: '#1e293b',
  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  fontSize: 13,
  headerFontSize: 13,
  headerFontWeight: 600,
  cellHorizontalPadding: 10,
  rowHeight: 28,
  headerHeight: 32,
});

const lightTheme = themeQuartz.withParams({
  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  fontSize: 13,
  headerFontSize: 13,
  headerFontWeight: 600,
  cellHorizontalPadding: 10,
  rowHeight: 28,
  headerHeight: 32,
});

const VIEW_MODES = [
  { id: 'merged', label: 'Merged' },
  { id: 'tabbed', label: 'Per Tenant' },
  { id: 'summary', label: 'Summary' },
];

const nullRenderer = (params) => {
  if (params.value === null || params.value === undefined) {
    return '<em style="color:#94a3b8">NULL</em>';
  }
  return String(params.value);
};

const MultiTenantResults = ({ multiResults, loading, error }) => {
  const [viewMode, setViewMode] = useState('merged');
  const [activeTenantTab, setActiveTenantTab] = useState('');
  const gridRef = useRef(null);
  const isDark = document.documentElement.classList.contains('dark');

  // Set first successful tenant as active tab
  React.useEffect(() => {
    if (multiResults?.results?.length > 0) {
      const first = multiResults.results.find(r => r.success);
      if (first) setActiveTenantTab(first.tenantName);
    }
  }, [multiResults]);

  // Auto-detect summary mode
  const isSummaryCandidate = useMemo(() => {
    if (!multiResults) return false;
    const successful = multiResults.results.filter(r => r.success);
    return successful.length > 0 && successful.every(r => r.rowCount === 1);
  }, [multiResults]);

  // ── Merged data ─────────────────────────────────────────────
  const mergedGrid = useMemo(() => {
    if (!multiResults) return { columnDefs: [], rowData: [] };
    const successful = multiResults.results.filter(r => r.success && r.columns.length > 0);
    if (successful.length === 0) return { columnDefs: [], rowData: [] };

    const dataColumns = successful[0].columns;
    const columnDefs = [
      { headerName: '#', valueGetter: p => p.node.rowIndex + 1, width: 60, pinned: 'left', sortable: false, filter: false, resizable: false, cellStyle: { color: '#94a3b8', textAlign: 'right', fontWeight: 500 } },
      { headerName: 'Tenant', field: '__tenant', pinned: 'left', width: 120, sortable: true, filter: true, cellStyle: { fontWeight: 600, color: '#3b82f6' } },
      ...dataColumns.map(col => ({ headerName: col, field: col, sortable: true, filter: true, resizable: true, minWidth: 80, cellRenderer: nullRenderer })),
    ];

    const rowData = [];
    for (const result of successful) {
      for (const row of result.rows) {
        const obj = { __tenant: result.tenantName };
        dataColumns.forEach((col, i) => { obj[col] = row[i]; });
        rowData.push(obj);
      }
    }
    return { columnDefs, rowData };
  }, [multiResults]);

  // ── Summary data ────────────────────────────────────────────
  const summaryGrid = useMemo(() => {
    if (!multiResults) return { columnDefs: [], rowData: [] };
    const successful = multiResults.results.filter(r => r.success && r.rowCount === 1);
    if (successful.length === 0) return { columnDefs: [], rowData: [] };

    const dataColumns = successful[0].columns;
    const columnDefs = [
      { headerName: 'Tenant', field: '__tenant', pinned: 'left', width: 120, sortable: true, filter: true, cellStyle: { fontWeight: 600, color: '#3b82f6' } },
      ...dataColumns.map(col => ({ headerName: col, field: col, sortable: true, filter: true, resizable: true, minWidth: 100, cellRenderer: nullRenderer })),
    ];

    const rowData = successful.map(r => {
      const obj = { __tenant: r.tenantName };
      dataColumns.forEach((col, i) => { obj[col] = r.rows[0][i]; });
      return obj;
    });

    // Totals row
    const totalsRow = { __tenant: 'TOTAL' };
    dataColumns.forEach(col => {
      const values = rowData.map(r => r[col]);
      const numericValues = values.filter(v => v !== null && v !== undefined && !isNaN(Number(v)));
      totalsRow[col] = numericValues.length === values.length && numericValues.length > 0
        ? numericValues.reduce((sum, v) => sum + Number(v), 0)
        : '';
    });
    rowData.push(totalsRow);

    return { columnDefs, rowData };
  }, [multiResults]);

  // ── Per-tenant grid ─────────────────────────────────────────
  const tabbedGrid = useMemo(() => {
    if (!multiResults || !activeTenantTab) return { columnDefs: [], rowData: [] };
    const result = multiResults.results.find(r => r.tenantName === activeTenantTab && r.success);
    if (!result || result.columns.length === 0) return { columnDefs: [], rowData: [] };

    const columnDefs = [
      { headerName: '#', valueGetter: p => p.node.rowIndex + 1, width: 60, pinned: 'left', sortable: false, filter: false, resizable: false, cellStyle: { color: '#94a3b8', textAlign: 'right', fontWeight: 500 } },
      ...result.columns.map(col => ({ headerName: col, field: col, sortable: true, filter: true, resizable: true, minWidth: 80, cellRenderer: nullRenderer })),
    ];

    const rowData = result.rows.map(row => {
      const obj = {};
      result.columns.forEach((col, i) => { obj[col] = row[i]; });
      return obj;
    });

    return { columnDefs, rowData };
  }, [multiResults, activeTenantTab]);

  const defaultColDef = useMemo(() => ({
    resizable: true, sortable: true, filter: true, minWidth: 80,
  }), []);

  const exportToExcel = useCallback(() => {
    let wsData;
    if (viewMode === 'summary' && isSummaryCandidate) {
      wsData = [summaryGrid.columnDefs.map(c => c.headerName), ...summaryGrid.rowData.map(r => summaryGrid.columnDefs.map(c => r[c.field] ?? ''))];
    } else if (viewMode === 'tabbed') {
      wsData = [tabbedGrid.columnDefs.filter(c => c.field).map(c => c.headerName), ...tabbedGrid.rowData.map(r => tabbedGrid.columnDefs.filter(c => c.field).map(c => r[c.field] ?? ''))];
    } else {
      wsData = [mergedGrid.columnDefs.filter(c => c.field).map(c => c.headerName), ...mergedGrid.rowData.map(r => mergedGrid.columnDefs.filter(c => c.field).map(c => r[c.field] ?? ''))];
    }
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Query Results');
    XLSX.writeFile(wb, `multi-tenant-results-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.xlsx`);
  }, [viewMode, isSummaryCandidate, summaryGrid, tabbedGrid, mergedGrid]);

  // ── States ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex-1 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950 p-8 flex items-center justify-center">
        <div className="text-center text-gray-500 dark:text-slate-400">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p>Executing across tenants...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950 p-8 flex items-center justify-center">
        <div className="text-center text-red-600"><p>{error}</p></div>
      </div>
    );
  }

  if (!multiResults) {
    return (
      <div className="flex-1 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950 p-8 flex items-center justify-center">
        <div className="text-center text-gray-400 dark:text-slate-500">
          <Database size={32} className="mx-auto mb-2 opacity-50" />
          <p>Run a query to see results here</p>
        </div>
      </div>
    );
  }

  const { results: tenantResults, totalRows, succeededTenants, failedTenants, totalDurationMs } = multiResults;

  const activeGrid = viewMode === 'summary' && isSummaryCandidate ? summaryGrid
    : viewMode === 'tabbed' ? tabbedGrid
    : mergedGrid;

  return (
    <div className="flex-1 border-t border-gray-200 dark:border-slate-700 flex flex-col min-h-0">
      {/* Status bar */}
      <div className="flex flex-wrap justify-between items-center px-3 py-1.5 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center gap-3 text-xs font-mono">
          <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
            <CheckCircle2 size={13} /> {succeededTenants}
          </span>
          {failedTenants > 0 && (
            <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
              <XCircle size={13} /> {failedTenants}
            </span>
          )}
          <span className="text-gray-500 dark:text-slate-400">{totalRows.toLocaleString()} rows</span>
          <span className="text-gray-500 dark:text-slate-400">{totalDurationMs}ms</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex rounded border border-gray-300 dark:border-slate-600 overflow-hidden">
            {VIEW_MODES.map((mode) => {
              if (mode.id === 'summary' && !isSummaryCandidate) return null;
              return (
                <button key={mode.id} type="button" onClick={() => setViewMode(mode.id)}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${viewMode === mode.id ? 'bg-blue-600 text-white' : 'bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800'} ${mode.id !== 'merged' ? 'border-l border-gray-300 dark:border-slate-600' : ''}`}>
                  {mode.label}
                </button>
              );
            })}
          </div>
          <button type="button" onClick={exportToExcel} className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">Export</button>
        </div>
      </div>

      {/* Failed tenants */}
      {failedTenants > 0 && (
        <div className="px-3 py-1.5 bg-red-50 dark:bg-red-950/30 border-b border-red-200 dark:border-red-800 flex flex-wrap gap-1.5">
          {tenantResults.filter(r => !r.success).map(r => (
            <span key={r.tenantName} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400">
              <AlertCircle size={11} /> {r.tenantName}
            </span>
          ))}
        </div>
      )}

      {/* Tenant tabs (tabbed mode) */}
      {viewMode === 'tabbed' && (
        <div className="flex border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto">
          {tenantResults.map(r => (
            <button key={r.tenantName} onClick={() => setActiveTenantTab(r.tenantName)}
              className={`px-4 py-1.5 text-xs font-medium border-b-2 whitespace-nowrap transition-colors -mb-px ${activeTenantTab === r.tenantName ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700'} ${!r.success ? 'text-red-500 dark:text-red-400' : ''}`}>
              {r.tenantName} <span className="text-gray-400 ml-1">{r.success ? r.rowCount : 'err'}</span>
            </button>
          ))}
        </div>
      )}

      {/* Tabbed error state */}
      {viewMode === 'tabbed' && (() => {
        const active = tenantResults.find(r => r.tenantName === activeTenantTab);
        if (active && !active.success) {
          return (
            <div className="p-8 text-center text-red-600 flex-1">
              <AlertCircle size={24} className="mx-auto mb-2" />
              <p className="font-medium">{active.tenantName}</p>
              <p className="text-sm mt-1">{active.error}</p>
            </div>
          );
        }
        return null;
      })()}

      {/* AG Grid */}
      {activeGrid.columnDefs.length > 0 && (
        <div className="flex-1 min-h-0">
          <AgGridReact
            ref={gridRef}
            key={`${viewMode}-${activeTenantTab}`}
            theme={isDark ? darkTheme : lightTheme}
            columnDefs={activeGrid.columnDefs}
            rowData={activeGrid.rowData}
            defaultColDef={defaultColDef}
            animateRows={false}
            enableCellTextSelection={true}
            ensureDomOrder={true}
            suppressColumnVirtualisation={activeGrid.columnDefs.length <= 50}
            getRowStyle={(params) => {
              if (viewMode === 'summary' && params.data?.__tenant === 'TOTAL') {
                return { fontWeight: 'bold', backgroundColor: isDark ? '#1e293b' : '#f1f5f9' };
              }
              return undefined;
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MultiTenantResults;
